import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import {
  copyText,
  formatMeetingDateTime,
  getApiErrorMessage,
  statusMeta,
} from "./meetingUtils"

// Active links use the green of "completed", revoked the red of "cancelled".
const LINK_STATUS = {
  active: { label: "Active", style: statusMeta("completed").chip },
  expired: { label: "Expired", style: statusMeta("unknown").chip },
  revoked: { label: "Revoked", style: statusMeta("cancelled").chip },
}

function LinkStatusChip({ status }) {
  const { label, style } = LINK_STATUS[status] || LINK_STATUS.expired
  return (
    <Chip
      size="small"
      label={label}
      variant="outlined"
      sx={{
        backgroundColor: style.background,
        color: style.text,
        borderColor: style.border,
        fontWeight: 600,
      }}
    />
  )
}

LinkStatusChip.propTypes = { status: PropTypes.string }

// Create, copy and turn off the booking links for one user. Anyone with a link
// can book meetings for THIS user only; the link expires after 30 days.
export default function BookingLinkDialog({ open, user, onClose }) {
  const [links, setLinks] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [label, setLabel] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [newLink, setNewLink] = useState(null)
  const [copyState, setCopyState] = useState("")
  const [revokingId, setRevokingId] = useState(null)
  const [errorMessage, setErrorMessage] = useState("")

  const userId = user?.id

  const loadLinks = useCallback(() => {
    if (!userId) return Promise.resolve()
    setIsLoading(true)
    return adminApi
      .fetchBookingLinks(userId)
      .then(({ data }) => setLinks(data || []))
      .catch((error) =>
        setErrorMessage(getApiErrorMessage(error, "Could not load the links."))
      )
      .finally(() => setIsLoading(false))
  }, [userId])

  useEffect(() => {
    if (!open) return
    setLinks([])
    setLabel("")
    setNewLink(null)
    setCopyState("")
    setErrorMessage("")
    loadLinks()
  }, [open, loadLinks])

  function handleCreate() {
    setIsCreating(true)
    setErrorMessage("")
    setCopyState("")
    adminApi
      .createBookingLink({ user_id: userId, label: label.trim() })
      .then(({ data }) => {
        setNewLink(data)
        setLabel("")
        return loadLinks()
      })
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not create the booking link.")
        )
      )
      .finally(() => setIsCreating(false))
  }

  async function handleCopy() {
    const copied = await copyText(newLink.url)
    setCopyState(copied ? "copied" : "failed")
  }

  function handleRevoke(link) {
    setRevokingId(link.id)
    setErrorMessage("")
    adminApi
      .revokeBookingLink(link.id)
      .then(({ data }) => {
        setLinks((current) =>
          current.map((item) => (item.id === data.id ? data : item))
        )
        if (newLink?.id === link.id) setNewLink(null)
      })
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not turn the link off.")
        )
      )
      .finally(() => setRevokingId(null))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Booking link for {user?.name || user?.email}</DialogTitle>
      <DialogContent dividers>
        <Column gap={2}>
          <Typography variant="body2" color="text.secondary">
            Share a link so someone without a login can book meetings for{" "}
            <strong>{user?.name || user?.email}</strong> only. The link cannot
            be changed to book for anyone else, expires after 30 days, and you
            can turn it off at any time.
          </Typography>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <Row gap={1.5} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Who is it for? (optional)"
              placeholder="For example: Sam, office assistant"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              inputProps={{ maxLength: 100 }}
              sx={{ minWidth: 280, flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={isCreating}
            >
              {isCreating ? "Creating..." : "Create link"}
            </Button>
          </Row>
          {newLink ? (
            <Paper
              variant="outlined"
              sx={{ p: 2, borderColor: "success.main" }}
              data-testid="new-booking-link"
            >
              <Column gap={1}>
                <Typography variant="subtitle2">
                  Copy this link now: for security it can&apos;t be shown again.
                </Typography>
                <Row gap={1} sx={{ alignItems: "center" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={newLink.url}
                    inputProps={{
                      readOnly: true,
                      "aria-label": "Booking link",
                      onFocus: (event) => event.target.select(),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyOutlinedIcon />}
                    onClick={handleCopy}
                  >
                    Copy
                  </Button>
                </Row>
                {copyState === "copied" ? (
                  <Typography variant="caption" color="success.main">
                    Link copied.
                  </Typography>
                ) : null}
                {copyState === "failed" ? (
                  <Typography variant="caption" color="error">
                    Could not copy automatically. Select the link above and copy
                    it.
                  </Typography>
                ) : null}
              </Column>
            </Paper>
          ) : null}
          {isLoading ? (
            <Row sx={{ justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Row>
          ) : null}
          {!isLoading && links.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No booking links yet.
            </Typography>
          ) : null}
          {links.length > 0 ? (
            <Table size="small" aria-label="Booking links">
              <TableHead>
                <TableRow>
                  <TableCell>For</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Meetings booked</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell>{link.label || "—"}</TableCell>
                    <TableCell>…{link.token_prefix}</TableCell>
                    <TableCell>
                      <LinkStatusChip status={link.status} />
                    </TableCell>
                    <TableCell>
                      {formatMeetingDateTime(link.created)}
                      {link.created_by_name ? ` · ${link.created_by_name}` : ""}
                    </TableCell>
                    <TableCell>
                      {formatMeetingDateTime(link.expires_at)}
                    </TableCell>
                    <TableCell align="right">{link.booking_count}</TableCell>
                    <TableCell align="right">
                      {link.status === "active" ? (
                        <Button
                          size="small"
                          color="error"
                          disabled={revokingId === link.id}
                          onClick={() => handleRevoke(link)}
                        >
                          Turn off
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

BookingLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
}
